// SnapshotDialog · Lot M5
// Saisie d'un snapshot performance : une section par plateforme active,
// stats plateformes (cumul) vs activité commerciale (période) STRICTEMENT séparés.
// Utilisé aussi pour "Superséder" une révision existante.

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "@/components/ui/dialog";
import { BarChart3, Activity, AlertCircle } from "lucide-react";

// Doit rester aligné avec CUMULATIVE_METRICS / PERIOD_METRICS backend.
const CUMULATIVE_FIELDS = [
  { key: "views",              label: "Vues" },
  { key: "favorites",          label: "Favoris" },
  { key: "leads",              label: "Leads" },
  { key: "messages",           label: "Messages" },
  { key: "calls",              label: "Appels" },
  { key: "financing_requests", label: "Demandes financement" },
];
const PERIOD_FIELDS = [
  { key: "visits",       label: "Visites" },
  { key: "test_drives",  label: "Essais" },
  { key: "offers",       label: "Offres" },
];

const inputCls =
  "h-10 w-full rounded-lg bg-white px-3 text-[14px] outline-none " +
  "focus:ring-2 focus:ring-[#C8FF2F]/40 font-mono";
const inputStyle = { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" };

function emptyBlock() {
  const cum = {}; CUMULATIVE_FIELDS.forEach(f => (cum[f.key] = ""));
  const per = {}; PERIOD_FIELDS.forEach(f => (per[f.key] = ""));
  return { cumulative: cum, period: per, external_listing_id: "" };
}

function parseBlock(block) {
  // "" → null (donnée inconnue) ; nombre valide → int/float ≥ 0
  const toNum = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) return "__invalid__";
    return n;
  };
  const out = { cumulative: {}, period: {} };
  for (const f of CUMULATIVE_FIELDS) out.cumulative[f.key] = toNum(block.cumulative[f.key]);
  for (const f of PERIOD_FIELDS) out.period[f.key] = toNum(block.period[f.key]);
  const listing = (block.external_listing_id || "").trim();
  if (listing) out.external_listing_id = listing;
  return out;
}

export default function SnapshotDialog({
  open, onOpenChange,
  mode = "create",         // "create" | "supersede"
  periodInfo,              // { period_index, period_start, period_end }
  activeChannels = [],     // [{ channel, label, external_listing_id?, ... }]
  initialByPlatform,       // pour supersede : pré-remplir avec les valeurs de l'orig
  currentRevision,         // pour supersede : rev en cours
  onSubmit,
}) {
  const [blocks, setBlocks] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Init/reset à l'ouverture
  useEffect(() => {
    if (!open) return;
    const next = {};
    activeChannels.forEach(c => {
      const b = emptyBlock();
      if (initialByPlatform && initialByPlatform[c.channel]) {
        const src = initialByPlatform[c.channel];
        const cum = src.cumulative || {};
        const per = src.period_direct || {};
        CUMULATIVE_FIELDS.forEach(f => {
          b.cumulative[f.key] = cum[f.key] == null ? "" : String(cum[f.key]);
        });
        PERIOD_FIELDS.forEach(f => {
          b.period[f.key] = per[f.key] == null ? "" : String(per[f.key]);
        });
        b.external_listing_id = src.external_listing_id || "";
      } else if (c.external_listing_id) {
        b.external_listing_id = c.external_listing_id;
      }
      next[c.channel] = b;
    });
    setBlocks(next);
    setNotes("");
  }, [open, activeChannels, initialByPlatform]);

  const updateField = (channel, kind, key, value) => {
    setBlocks(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [kind]: { ...prev[channel][kind], [key]: value },
      },
    }));
  };

  const updateListing = (channel, value) => {
    setBlocks(prev => ({
      ...prev,
      [channel]: { ...prev[channel], external_listing_id: value },
    }));
  };

  const parsed = useMemo(() => {
    const by_platform = {};
    let hasAnyValue = false;
    let hasInvalid = false;
    for (const ch of activeChannels) {
      const b = blocks[ch.channel];
      if (!b) continue;
      const p = parseBlock(b);
      // Check invalid
      for (const kind of ["cumulative", "period"]) {
        for (const v of Object.values(p[kind])) {
          if (v === "__invalid__") hasInvalid = true;
          else if (v !== null) hasAnyValue = true;
        }
      }
      by_platform[ch.channel] = p;
    }
    return { by_platform, hasAnyValue, hasInvalid };
  }, [blocks, activeChannels]);

  const submit = async (e) => {
    e.preventDefault();
    if (parsed.hasInvalid) {
      return toast.error("Valeurs négatives ou non-numériques détectées");
    }
    if (!parsed.hasAnyValue) {
      return toast.error("Au moins une métrique doit être renseignée");
    }
    setSubmitting(true);
    try {
      const payload = { by_platform: parsed.by_platform, notes: notes.trim() };
      if (mode === "create") payload.period_index = periodInfo?.period_index;
      await onSubmit?.(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const isSupersede = mode === "supersede";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[880px] max-h-[92vh] overflow-hidden flex flex-col"
        data-testid={isSupersede ? "supersede-dialog" : "snapshot-dialog"}
      >
        <DialogHeader>
          <DialogTitle className="text-[19px] font-semibold tracking-[-0.01em]">
            {isSupersede
              ? `Corriger le snapshot — révision ${(currentRevision || 1) + 1}`
              : "Nouveau snapshot hebdomadaire"}
          </DialogTitle>
          <p className="text-[12.5px] text-black/55 max-w-[640px]">
            {isSupersede
              ? "La révision courante sera archivée mais restera consultable. Une nouvelle révision est créée avec les valeurs ci-dessous."
              : `Période #${periodInfo?.period_index}. Renseignez les métriques pour chaque plateforme active. Laissez vide si la valeur n'est pas connue (≠ 0).`
            }
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 flex-1 overflow-y-auto pr-2 space-y-6">
          {activeChannels.length === 0 && (
            <div className="rounded-2xl bg-black/[0.03] px-5 py-4 text-[13px] text-black/55">
              Aucun canal actif — activez au moins un canal pour capturer un snapshot.
            </div>
          )}

          {activeChannels.map(ch => (
            <section
              key={ch.channel}
              className="rounded-2xl bg-black/[0.02] p-5"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
              data-testid={`snap-platform-${ch.channel}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
                    Plateforme
                  </div>
                  <div className="mt-0.5 text-[15px] font-semibold text-black">
                    {ch.label || ch.channel}
                  </div>
                </div>
                <label className="flex flex-col gap-1 text-right">
                  <span className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
                    ID d&apos;annonce externe (optionnel)
                  </span>
                  <input
                    type="text"
                    value={blocks[ch.channel]?.external_listing_id || ""}
                    onChange={e => updateListing(ch.channel, e.target.value)}
                    placeholder="ex. lbc-12345"
                    className={`${inputCls} max-w-[260px] text-right`}
                    style={inputStyle}
                    data-testid={`snap-${ch.channel}-listing`}
                  />
                </label>
              </div>

              {/* Section CUMUL (stats plateforme) */}
              <div className="mt-4 rounded-xl bg-white/70 p-4"
                   style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-black/60">
                  <BarChart3 size={12} />
                  Stats plateforme (cumul depuis publication)
                </div>
                <p className="mt-1 text-[11.5px] text-black/45">
                  Compteurs cumulatifs du canal — le delta période est calculé automatiquement.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
                  {CUMULATIVE_FIELDS.map(f => (
                    <label key={f.key} className="flex flex-col gap-1">
                      <span className="text-[10.5px] uppercase tracking-[0.14em] text-black/55">
                        {f.label}
                      </span>
                      <input
                        type="number" min={0} step={1}
                        value={blocks[ch.channel]?.cumulative[f.key] ?? ""}
                        onChange={e => updateField(ch.channel, "cumulative", f.key, e.target.value)}
                        placeholder="—"
                        className={inputCls} style={inputStyle}
                        data-testid={`snap-${ch.channel}-cum-${f.key}`}
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Section PÉRIODE (activité commerciale) */}
              <div className="mt-4 rounded-xl bg-white/70 p-4"
                   style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-black/60">
                  <Activity size={12} />
                  Activité commerciale (sur la période uniquement)
                </div>
                <p className="mt-1 text-[11.5px] text-black/45">
                  Valeurs directes de la période — jamais dérivées d&apos;un cumul.
                </p>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  {PERIOD_FIELDS.map(f => (
                    <label key={f.key} className="flex flex-col gap-1">
                      <span className="text-[10.5px] uppercase tracking-[0.14em] text-black/55">
                        {f.label}
                      </span>
                      <input
                        type="number" min={0} step={1}
                        value={blocks[ch.channel]?.period[f.key] ?? ""}
                        onChange={e => updateField(ch.channel, "period", f.key, e.target.value)}
                        placeholder="—"
                        className={inputCls} style={inputStyle}
                        data-testid={`snap-${ch.channel}-per-${f.key}`}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </section>
          ))}

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              Notes (optionnel)
            </span>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Ex. Canal Leboncoin remis en ligne mardi"
              className="rounded-lg bg-white px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-[#C8FF2F]/40 resize-none"
              style={inputStyle}
              data-testid="snap-notes"
            />
          </label>

          {parsed.hasInvalid && (
            <div className="flex items-center gap-2 rounded-xl bg-[#FEF2F2] p-3 text-[12.5px] text-[#B91C1C]">
              <AlertCircle size={14} />
              Une ou plusieurs valeurs sont invalides (négatives ou non-numériques).
            </div>
          )}
        </form>

        <DialogFooter className="mt-3 gap-2 sm:justify-end shrink-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/70 hover:text-black"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
            data-testid="snap-cancel-btn"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || parsed.hasInvalid || !parsed.hasAnyValue || activeChannels.length === 0}
            className="rounded-full bg-[#C8FF2F] px-5 py-2 text-[13px] font-medium text-black transition-transform hover:scale-[1.005] disabled:opacity-40 disabled:hover:scale-100"
            data-testid="snap-submit-btn"
          >
            {submitting
              ? (isSupersede ? "Correction en cours…" : "Capture en cours…")
              : (isSupersede ? "Créer la révision" : "Capturer le snapshot")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
